<script lang="ts">
	import { get } from 'svelte/store';
	// Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	// Design system components
	import {
		Button,
		Toggle,
		Checkbox,
		Alert,
		Modal,
		Container,
		Text,
		Loader,
	} from '@Shelly/shelly-design-system';
	// Helpers
	import { real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar, wait } from 'src/helpers/utilities';
	import {
		extractWallDisplayComponent,
		deleteWallDisplayExtention,
		isWallDisplaySeparateComponentsSupported,
		disableWallDisplayExtention,
		showSuccessToast,
	} from 'src/helpers/wall_display_extract/extracting_helpers';
	// Stores
		import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
	// Types
	import type { Device } from 'src/types/Device';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	export let device: Device;

	let id = real_id(device.id);
	let loading = false;

	let showModalPrompt = false;
	$: extractionValueToggle = typeof get(devices)[`${real_id(device.id)}_101`] !== 'undefined';
	let extractionValueToggleInModal = false;

	$: settingsStore = getDeviceSettings(device.id);
	$: enabled = typeof $settingsStore?.['thermostat:0'] !== 'undefined';

	const isExtractionSupported = isWallDisplaySeparateComponentsSupported(
		readDeviceSettings(device.id),
	);

	const deleteExtractedThermostat = async () => {
		await deleteWallDisplayExtention({
			id: real_id(device.id) + '_101',
		} as Device);
	};

	const initThermostat = async () => {
		await gen2Request(device, 'Thermostat.Create', {});

		if (isExtractionSupported && extractionValueToggle) {
			await extractWallDisplayComponent(device, 'thermostat');
		}
	};

	const enableThermostat = async () => {
		loading = true;
		const nextEnabledState = !enabled;

		if (nextEnabledState) {
			await initThermostat();
		} else {
			await disableWallDisplayExtention({
				id: real_id(device.id) + '_101',
			} as Device);
		}
		await wait(5000);
		loading = false;
		setSidePanelState({ type: 'device', id: device.id, showSettingsModal: false });
	};

	const enablExtractThermostat = async () => {
		const nextEnabledState = !extractionValueToggle;

		if (nextEnabledState) {
			await extractWallDisplayComponent(device, 'thermostat');
		} else {
			await deleteExtractedThermostat();
		}
	};

	const handleExtractEnableDevice = async () => {
		showModalPrompt = false;
		loading = true;
		await Promise.all([enableThermostat(), enablExtractThermostat()]);
		showSuccessToast();
		await wait(5000);
		loading = false;
		setSidePanelState({ type: 'device', id: device.id, showSettingsModal: false });
	};
</script>

{#if !isExtractionSupported}
	<Checkbox
		size="l"
		class="mb-s"
		bind:checked={enabled}
		on:click={() => {
			enableThermostat();
			showSuccessToast();
		}}
		id="checkbox-d7xeqgw"
	>
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>
{/if}

{#if isExtractionSupported}
	{#if loading}
		<Loader size="m" class="align-self-center" />
	{:else}
		<Checkbox
			size="l"
			class="mb-s"
			bind:checked={enabled}
			on:click={() => (showModalPrompt = true)}
			id="checkbox-3akx59s"
		>
			{capFirstChar($format('option_enable', { default: 'enable' }))}
		</Checkbox>
		<Toggle
			checked={extractionValueToggle}
			size="m"
			disabled={!enabled}
			on:click={async () => {
				await enablExtractThermostat();
				showSuccessToast();
			}}
			id="toggle-cxo8why"
		>
			{$format('extract_thermostat_extension', {
				default: 'Extract Thermostat extension',
			})}
		</Toggle>
		<Alert
			type="info"
			heading={$format('alert_heading_extracted_thermostat_wall_display', {
				default:
					'The extracted thermostat will operate as an independent device, functioning separately from the wall display.',
			})}
		/>
	{/if}

	<Modal
		bind:showModal={showModalPrompt}
		on:close={() => (showModalPrompt = false)}
		title={!enabled
			? capFirstChar(
					$format('modal_title_enable_thermostat', {
						default: 'Enable thermostat',
					}),
				)
			: $format('modal_title_disable_thermostat', {
					default: 'Disable thermostat',
				})}
	>
		<svelte:fragment slot="content">
			<Container padding="0">
				<Text class="mb-m"
					>{$format('text_settings_soft_reboot_dialog', {
						default:
							'Changing this setting will soft reboot the device. Do you want to continue?',
					})}</Text
				>
				{#if !enabled}
					<Toggle
						checked={extractionValueToggleInModal}
						size="m"
						on:click={() => {
							extractionValueToggleInModal = !extractionValueToggleInModal;
						}}
						id="toggle-77cj72e"
					>
						{$format('toggle_extract_thermostat_as_well', {
							default: 'Would you like to extract the thermostat as well?',
						})}
					</Toggle>
				{/if}
			</Container>
		</svelte:fragment>

		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="width-md-m width-sm-full mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={async () => {
					showModalPrompt = false;
				}}>{capFirstChar($format('button_cancel', { default: 'Cancel' }))}</Button
			>
			<Button
				class="width-md-m width-sm-full"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={async () => {
					showModalPrompt = false;

					if (extractionValueToggleInModal) {
						await handleExtractEnableDevice();
					} else {
						await enableThermostat();
					}
					showSuccessToast();

					setSidePanelState({ showSettingsModal: false });
				}}
				>{!enabled
					? capFirstChar($format('button_enable', { default: 'Enable' }))
					: capFirstChar($format('button_disable', { default: 'Disable' }))}</Button
			>
		</div>
	</Modal>
{/if}
